import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ModuleTypes } from 'src/app/modules/settings/enums/moduleTypes';
import { DepartmentVisibilities } from 'src/app/modules/shared/enums/departmentVisibility.enum';
import { EmployeeRoleTypes } from 'src/app/modules/shared/enums/employees/EmployeeRoleTypes';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { EmployeeSettingsService } from 'src/app/modules/shared/services/employee-settings.service';
import { EmployeeUtil } from 'src/app/modules/shared/utilities/employee.utilities';
import { EmployeeDetailsViewModel } from 'src/app/modules/shared/viewModels/employeeDetailsViewModel';
import { PermissionViewModel } from 'src/app/modules/shared/viewModels/permissionViewModel';
import { Account } from 'src/app/modules/shared/models/account';
import { Employee } from 'src/app/modules/shared/models/employee';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { Subscription, forkJoin } from 'rxjs';
import { AllowedFiltersService } from 'src/app/modules/shared/services/allowed-filters.service';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { TranslateService } from '@ngx-translate/core';
import { T } from 'src/assets/i18n/translation-keys';
import { AccountSettingsService } from 'src/app/modules/shared/services/account-settings.service';
import {
  DataType,
  TableHeader,
} from 'src/app/modules/shared/components/common/responsive-table/responsive-table/responsive-table.component';
import { EmployeeRole } from 'src/app/modules/shared/employeeRole';
import { PermissionHelper } from 'src/app/modules/settings/utils/permission.helper';

enum ReadWriteTypes {
  read = 'read',
  write = 'write',
}

@Component({
    selector: 'app-planning-employee-permissions',
    templateUrl: './planning-employee-permissions.component.html',
    styleUrls: ['./planning-employee-permissions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PlanningEmployeePermissionsComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('canEdit') canEdit: TemplateRef<ElementRef<HTMLElement>>;
  @ViewChild('canSee') canSee: TemplateRef<ElementRef<HTMLElement>>;
  @ViewChild('isAdmin') isAdmin: TemplateRef<ElementRef<HTMLElement>>;
  @Input() employeeDetailsViewModel: EmployeeDetailsViewModel;
  @Input() isInPrimaryRole: boolean;
  @Input() isAccountAdmin: boolean;
  @Input() isDepartmentSelected: boolean;

  public itemsPerPage = 20;
  public childAccountPermissions: { AccountId: number; canSee: boolean; canEdit: boolean }[] = [];
  public selectedObjects: Account[] = [];
  public childAccounts: Account[];
  public readProjectsFilters: FilterViewModel[] = [];
  public writeProjectsFilters: FilterViewModel[] = [];

  public allDepartments: DepartmentVisibilities = DepartmentVisibilities.All_Departments;
  public readProjectsNonConfidentialDepVisibility: DepartmentVisibilities;
  public writeProjectsNonConfidentialDepVisibility: DepartmentVisibilities;
  public readRisksNonConfidentialDepVisibility: DepartmentVisibilities;
  public writeRisksNonConfidentialDepVisibility: DepartmentVisibilities;

  public projectsReadNonConfidentialFilters: FilterViewModel[] = [];
  public confidentialProjectIDs: number[];
  public confidentialProjectFilters: FilterViewModel[];
  public allowedConfidentialProjectFilters: FilterViewModel[];

  public readonly employeeRoleTypes = EmployeeRoleTypes;
  public readonly filterTypes = FilterTypes;
  public readonly departmentVisibilites = DepartmentVisibilities;
  public readonly moduleTypes = ModuleTypes;
  public readonly readWriteTypes = ReadWriteTypes;
  public isDropdownLoading = false;
  public readonly T = T;

  public account: Account;
  private employee: Employee;
  private subscriptions = new Subscription();
  private allowedFilters: FilterViewModel[] = [];

  localisedEmployee: string = 'Employee';
  localisedEmployees: string = 'Employees';
  localisedTasks: string = 'Tasks';
  localisedDepartment: string = 'Department';
  localisedDepartments: string = 'Departments';
  localisedProject: string = 'Project';
  localisedProjects: string = 'Projects';

  constructor(
    private readonly accountSettingsService: AccountSettingsService,
    private readonly authenticationService: AuthenticationService,
    private readonly employeeSettingsService: EmployeeSettingsService,
    private readonly allowedFiltersService: AllowedFiltersService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly localisationService: LocalisationService,
    private readonly translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.employee = this.authenticationService.getCurrentEmployee();
    this.account = this.authenticationService.getCurrentAccount();
    this.initDepartmentVisibilities();
    this.initAllowedFilters();

    this.isDropdownLoading = true;

    this.subscriptions.add(
      forkJoin([
        this.employeeSettingsService.getConfidentialIDs(ObjectTypes.Project),
        this.accountSettingsService.getChildAccounts(),
      ]).subscribe((res: any) => {
        this.childAccounts = res[1];
        this.allowedConfidentialProjectFilters = res[0];
        this.confidentialProjectIDs = this.allowedConfidentialProjectFilters.map((f) => f.filterValue);
        this.isDropdownLoading = false;
        this.changeDetectorRef.detectChanges();
      })
    );

    this.isReadAndWriteTheSame();
    this.initLocalisations();
  }

  get tableHeaders(): TableHeader[] {
    return [
      {
        dataType: DataType.String,
        title: 'Title',
        property: 'title',
      },
      {
        dataType: DataType.Template,
        title: 'Can See',
        template: this.canSee,
      },
      {
        dataType: DataType.Template,
        title: 'Can Edit',
        template: this.canEdit,
      },
      {
        dataType: DataType.Template,
        title: 'Admin',
        template: this.isAdmin,
      },
    ];
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges() {
    this.initDepartmentVisibilities();
    this.initAllFiltersByType();
  }

  initLocalisations() {
    this.localisedEmployee = this.localisationService.localiseObjectType(ObjectTypes.Employee);
    this.localisedEmployees = this.localisationService.localiseObjectType(ObjectTypes.Employee, true);
    this.localisedDepartment = this.localisationService.localiseObjectType(ObjectTypes.Department);
    this.localisedDepartments = this.localisationService.localiseObjectType(ObjectTypes.Department, true);
    this.localisedProject = this.localisationService.localiseObjectType(ObjectTypes.Project);
    this.localisedProjects = this.localisationService.localiseObjectType(ObjectTypes.Project, true);
    this.localisedTasks = this.localisationService.localiseObjectType(ObjectTypes.Task, true);
    this.changeDetectorRef.markForCheck();
  }

  hasAccessToModule() {
    return EmployeeUtil.hasAccessToModule(this.employeeDetailsViewModel, ModuleTypes.Planning);
  }

  isInRole(employeeRole: EmployeeRoleTypes) {
    return EmployeeUtil.hasRole(this.employeeDetailsViewModel, employeeRole);
  }

  selectPlanningUserRole(isAdd: boolean) {
    if (isAdd) {
      this.employeeDetailsViewModel.modulePermissionViewModels.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        module: ModuleTypes.Planning,
        objectType: 0,
        createdById: this.employee.id,
        created: '',
        accountId: this.employee.accountId,
      });
      this.readProjectsNonConfidentialDepVisibility = DepartmentVisibilities.All_Departments;
      this.writeProjectsNonConfidentialDepVisibility = DepartmentVisibilities.All_Departments;
      this.readRisksNonConfidentialDepVisibility = DepartmentVisibilities.All_Departments;
      this.writeRisksNonConfidentialDepVisibility = DepartmentVisibilities.Nothing;
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(FilterTypes.Project, 0, this.readProjectsNonConfidentialDepVisibility, ReadWriteTypes.read, false)
      );
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          FilterTypes.Project,
          0,
          this.writeProjectsNonConfidentialDepVisibility,
          ReadWriteTypes.write,
          false
        )
      );
    } else {
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
        (p) => p.module !== ModuleTypes.Planning
      );
      this.readProjectsFilters = [];
      this.writeProjectsFilters = [];
      this.employeeDetailsViewModel.modulePermissionViewModels = this.employeeDetailsViewModel.modulePermissionViewModels.filter(
        (r) => r.module !== ModuleTypes.Planning
      );
      this.employeeDetailsViewModel.employeeRole = this.employeeDetailsViewModel.employeeRole.filter(
        (r) => r.roleId !== EmployeeRoleTypes.Project_Admin
      );
    }
    this.initDepartmentVisibilities();
    this.initAllFiltersByType();
    this.changeDetectorRef.detectChanges();
  }

  private initDepartmentVisibilities() {
    this.readProjectsNonConfidentialDepVisibility = this.getDepartmentVisibility(
      ModuleTypes.Planning,
      FilterTypes.Project,
      ReadWriteTypes.read
    );
    this.writeProjectsNonConfidentialDepVisibility = this.getDepartmentVisibility(
      ModuleTypes.Planning,
      FilterTypes.Project,
      ReadWriteTypes.write
    );
  }

  getDepartmentVisibility(module: ModuleTypes, filterType: FilterTypes, readOrWrite: ReadWriteTypes, confidential = false) {
    return PermissionHelper.getDepartmentVisibility(this.employeeDetailsViewModel, readOrWrite, filterType, module, confidential);
  }

  changeDepartmentVisibility(
    currentDepVisibility: DepartmentVisibilities,
    filterType: FilterTypes,
    readOrWrite: ReadWriteTypes,
    confidential = false
  ) {

    PermissionHelper.changeDepartmentVisibility(this.employeeDetailsViewModel, currentDepVisibility, this.moduleTypes.Planning, filterType, readOrWrite, confidential);

    this.initDepartmentVisibilities();
  }

  isCanSee(object: any) {
    return this.employeeDetailsViewModel.childAccountPermissions.find(
      (d) => d.accountId == object.id && d.type === FilterTypes.Project && d.read
    );
  }

  selectCanSee(event: boolean, object: any) {
    const accountID = object.id;

    if (!this.employeeDetailsViewModel.childAccountPermissions) {
      this.employeeDetailsViewModel.childAccountPermissions = [];
    }

    const alreadyPermissions = this.employeeDetailsViewModel.childAccountPermissions.find(
      (d) => d.accountId === accountID && d.type === FilterTypes.Project
    );
    if (alreadyPermissions) {
      alreadyPermissions.read = event;

      if (!event) {
        alreadyPermissions.write = false;
      }
    } else {
      const permission = this.generatePermission(
        FilterTypes.Project,
        0,
        DepartmentVisibilities.All_Departments,
        ReadWriteTypes.read,
        false,
        accountID
      );
      permission.accountId = accountID;
      permission.read = event;
      this.employeeDetailsViewModel.childAccountPermissions.push(permission);
    }
  }

  isCanEdit(object: any) {
    const accountID = object.id;
    const canEditPermission = this.employeeDetailsViewModel.childAccountPermissions.find(
      (d) => d.accountId === accountID && d.type === FilterTypes.Project && d.write
    );
    return !!canEditPermission;
  }

  selectCanEdit(event: boolean, object: any) {
    const accountID = object.id;

    if (!this.employeeDetailsViewModel.childAccountPermissions) {
      this.employeeDetailsViewModel.childAccountPermissions = [];
    }

    const alreadyPermissions = this.employeeDetailsViewModel.childAccountPermissions.find(
      (d) => d.accountId === accountID && d.type === FilterTypes.Project
    );

    if (alreadyPermissions) {
      alreadyPermissions.write = event;
      alreadyPermissions.read = event;
    } else {
      const permission = this.generatePermission(
        FilterTypes.Project,
        0,
        DepartmentVisibilities.All_Departments,
        ReadWriteTypes.write,
        false,
        accountID
      );

      permission.read = event;
      permission.write = event;
      this.employeeDetailsViewModel.childAccountPermissions.push(permission);
    }
  }

  isAdminSelected(object: any): boolean {
    if (!this.employeeDetailsViewModel.childEmployeeRole) return false;

    const accountID = object.id;
    const admin = this.employeeDetailsViewModel.childEmployeeRole.find(
      (d) => d.accountId === accountID && d.roleId === EmployeeRoleTypes.Project_Admin
    );
    return admin !== null;
  }

  selectAdmin(event: boolean, object: any) {
    const accountID = object.id;

    if (!this.employeeDetailsViewModel.childAccountPermissions) {
      this.employeeDetailsViewModel.childAccountPermissions = [];
    }

    if (event) {
      const alreadyPermissions = this.employeeDetailsViewModel.childAccountPermissions.find(
        (d) => d.accountId === accountID && d.type === FilterTypes.Project
      );

      if (alreadyPermissions) {
        this.employeeDetailsViewModel.childAccountPermissions = this.employeeDetailsViewModel.childAccountPermissions.filter(
          (d) => d.accountId !== accountID && d.type === FilterTypes.Project
        );
      }

      const userRole: EmployeeRole[] = [
        this.generateUserRole(this.employeeDetailsViewModel.id, EmployeeRoleTypes.General_User, accountID),
        this.generateUserRole(this.employeeDetailsViewModel.id, EmployeeRoleTypes.Project_Admin, accountID),
      ];

      const permissions: PermissionViewModel[] = [
        this.generatePermission(
          FilterTypes.Project,
          ModuleTypes.Planning,
          DepartmentVisibilities.All_Departments,
          ReadWriteTypes.write,
          false,
          accountID
        ),
        this.generatePermission(
          FilterTypes.Task_Group,
          ModuleTypes.Planning,
          DepartmentVisibilities.All_Departments,
          ReadWriteTypes.write,
          false,
          accountID
        ),
        this.generatePermission(
          FilterTypes.Task,
          ModuleTypes.Planning,
          DepartmentVisibilities.All_Departments,
          ReadWriteTypes.write,
          false,
          accountID
        ),
      ];

      if (!this.employeeDetailsViewModel.childEmployeeRole) {
        this.employeeDetailsViewModel.childEmployeeRole = [];
      }

      this.employeeDetailsViewModel.childEmployeeRole.push(...userRole);
      this.employeeDetailsViewModel.childAccountPermissions.push(...permissions);
    } else {
      this.employeeDetailsViewModel.childEmployeeRole = this.employeeDetailsViewModel.childEmployeeRole.filter(
        (d) => d.accountId === accountID && d.roleId !== EmployeeRoleTypes.Project_Admin
      );
    }
  }

  selectProjectAdmin(isAdd: boolean) {
    if (isAdd) {
      this.employeeDetailsViewModel.employeeRole.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        roleId: EmployeeRoleTypes.Project_Admin,
        created: '',
        accountId: 0,
      });
    } else {
      this.employeeDetailsViewModel.employeeRole = this.employeeDetailsViewModel.employeeRole.filter(
        (r) => r.roleId !== EmployeeRoleTypes.Project_Admin
      );
    }
    this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
      (p) => !(p.module === ModuleTypes.Planning && p.type === FilterTypes.Project && !p.confidential)
    );
    this.employeeDetailsViewModel.permissions.push(
      this.generatePermission(FilterTypes.Project, 0, DepartmentVisibilities.All_Departments, ReadWriteTypes.read, false)
    );
    this.employeeDetailsViewModel.permissions.push(
      this.generatePermission(FilterTypes.Project, 0, DepartmentVisibilities.All_Departments, ReadWriteTypes.write, false)
    );
    this.readProjectsFilters = [];
    this.writeProjectsFilters = [];
    this.initDepartmentVisibilities();
  }

  hasWritePermission(type: FilterTypes, values: number[], confidential: boolean): boolean {
    let hasWritePermission = false;

    if (this.isInPrimaryRole) {
      hasWritePermission = true;
    } else if (this.employeeDetailsViewModel.permissions) {
      const permission = this.employeeDetailsViewModel.permissions.find(
        (p) =>
          p.module === ModuleTypes.Planning &&
          (p.type === type || p.type === FilterTypes.Empty) &&
          p.write &&
          (p.value === 0 || values.indexOf(p.value) !== -1) &&
          p.confidential === confidential
      );
      if (permission) {
        hasWritePermission = true;
      }
    }

    return hasWritePermission;
  }

  initAllowedFilters() {
    this.allowedFilters = this.allowedFiltersService.getCachedAllAllowedFilters();
    this.initAllFiltersByType();
  }

  initAllFiltersByType() {
    this.readProjectsFilters = this.filtersByType(FilterTypes.Project, ReadWriteTypes.read, false);
    this.writeProjectsFilters = this.filtersByType(FilterTypes.Project, ReadWriteTypes.write, false);
    this.confidentialProjectFilters = this.filtersByType(FilterTypes.Project, ReadWriteTypes.write, true);
  }

  private filtersByType(type: FilterTypes, readOrWrite: ReadWriteTypes, confidential: boolean): FilterViewModel[] {
    let foundedPermissions: PermissionViewModel[];

    if (readOrWrite === ReadWriteTypes.read) {
      foundedPermissions = this.employeeDetailsViewModel.permissions.filter(
        (p) => p.module === ModuleTypes.Planning && p.type === type && p.read && p.value !== -1 && p.confidential == confidential
      );
      if (foundedPermissions.some((p) => p.value === 0)) {
        foundedPermissions = [];
      }
    } else {
      foundedPermissions = this.employeeDetailsViewModel.permissions.filter(
        (p) =>
          p.module === ModuleTypes.Planning &&
          p.type === type &&
          p.write &&
          p.value !== -1 &&
          p.value !== 0 &&
          p.confidential === confidential
      );
    }

    const filters: Partial<FilterViewModel>[] = [];
    if (foundedPermissions?.length > 0) {
      foundedPermissions.forEach((p) => {
        const checkIfFilterIsAlreadyIncluded = filters.find((f) => f.filterType === p.type && f.filterValue == p.value);
        if (!checkIfFilterIsAlreadyIncluded) {
          const findedFilter = this.allowedFilters.find((f) => f.filterType === p.type && f.filterValue == p.value);
          if (findedFilter) {
            filters.push(findedFilter);
          }
        }
      });
    }

    return filters as FilterViewModel[];
  }


  /**
   * We are Using this when changing AllProject checkbox
   * 1.Check first if its read or Write filter
   * 2.Based on IsAdd(adding or removing permission) if its FALSE we need to remove that permission
   * 3.Else we need to add the permission but first if Permission is 0 (ALL)
   *    3.1 We need to remove all other permissions because we need only 0 for ALL items
   *    3.2 Also If there is 0 (All items) for WRITE we need to remove also the READ items
   *    3.3 We need to remove all local Filters (To clear the dropdown if there are any selected filters)
   */
  changePermission(
    isAdd: boolean,
    filterType: FilterTypes,
    value: number,
    readOrWrite: ReadWriteTypes,
    depVisibility: DepartmentVisibilities,
    confidential: boolean,
    object?: any
  ) {
    const accountId = object ? object.id : 0;
    if (readOrWrite == ReadWriteTypes.read) {
      // Check if is Add
      if (!isAdd) {
        this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
          (p) => !(p.module === ModuleTypes.Planning && p.type === filterType && p.value === value && p.read && !p.write)
        );
        this.initAllFiltersByType();
      } else {
        // Clear all other permissions if current value is 0 (All)
        // Also we need to clear all LocalFilters (FilterViewModels)
        if (value === 0) {
          this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
            (p) => !(p.module == ModuleTypes.Planning && p.type === filterType && p.read && !p.write)
          );
          this.setFilters([], filterType, ReadWriteTypes.read, confidential);
        }
        this.employeeDetailsViewModel.permissions.push(
          this.generatePermission(filterType, value, depVisibility, ReadWriteTypes.read, confidential, accountId)
        );
      }
    } else {
      if (!isAdd) {
        this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
          (p) =>
            !(
              p.module === ModuleTypes.Planning &&
              p.type === filterType &&
              p.value === value &&
              p.write &&
              p.confidential === confidential
            )
        );
        if (confidential) {
          this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
            (p) =>
              !(
                p.module === ModuleTypes.Planning &&
                p.type === filterType &&
                p.value === value &&
                p.read &&
                p.confidential === confidential
              )
          );
        }
      } else {
        // Clear all other permissions if current value is 0 (All)
        // Also we need to clear all LocalFilters (FilterViewModels)
        if (value === 0) {
          this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
            (p) => !(p.module === ModuleTypes.Planning && p.type === filterType && p.write && p.confidential == confidential)
          );
          this.setFilters([], filterType, ReadWriteTypes.write, confidential);
          // IF is WRITE for ALL items (0) we need to refresh the READ props also --------------
          if (depVisibility == DepartmentVisibilities.All_Departments) {
            this.setFilters([], filterType, ReadWriteTypes.read, confidential);
            this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
              (p) =>
                !(
                  p.module === ModuleTypes.Planning &&
                  p.type === filterType &&
                  p.read &&
                  p.write &&
                  p.confidential == confidential
                )
            );
            this.employeeDetailsViewModel.permissions.push(
              this.generatePermission(
                filterType,
                0,
                DepartmentVisibilities.All_Departments,
                ReadWriteTypes.write,
                confidential,
                accountId
              )
            );
          } else if (depVisibility === DepartmentVisibilities.Only_User_Department) {
            // This is for the case when we select ALL EDIT Permisions TO BE ONLY USER DEPT but some READ PERMISIONS are with DeptVisibility.Nothing
            // So we need to check if there are DepartmentVisibilities.Nothing and set them to be DepartmentVisibilites.Only_User_dept
            const existintDeptVisibWithNothin = this.employeeDetailsViewModel.permissions.filter(
              (p) =>
                p.module === ModuleTypes.Planning &&
                p.type === filterType &&
                p.read &&
                p.write &&
                p.departmentVisibility === DepartmentVisibilities.Nothing &&
                p.confidential === confidential
            );
            if (existintDeptVisibWithNothin && existintDeptVisibWithNothin.length > 0) {
              this.setFilters([], filterType, ReadWriteTypes.read, confidential);
              this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
                (p) =>
                  !(
                    p.module === ModuleTypes.Planning &&
                    p.type === filterType &&
                    p.read &&
                    !p.write &&
                    p.departmentVisibility === DepartmentVisibilities.Nothing &&
                    p.confidential === confidential
                  )
              );
              this.employeeDetailsViewModel.permissions.push(
                this.generatePermission(
                  filterType,
                  0,
                  DepartmentVisibilities.Only_User_Department,
                  ReadWriteTypes.write,
                  confidential,
                  accountId
                )
              );
            }
          } else {
            this.employeeDetailsViewModel.permissions.push(
              this.generatePermission(filterType, value, depVisibility, ReadWriteTypes.write, confidential, accountId)
            );
          }
        }
      }
    }
  }

  makeWriteSameAsRead(
    filterType: FilterTypes,
    depVisibility: DepartmentVisibilities,
    readOrWrite: ReadWriteTypes,
    confidential: boolean
  ) {
    let permissions = [];
    if (this.writeProjectsFilters.length === 0) {
      this.writeProjectsFilters = this.filtersByType(FilterTypes.Project, ReadWriteTypes.read, false);
      this.writeProjectsFilters.forEach((f) => {
        permissions.push(this.generatePermission(filterType, f.filterValue, depVisibility, readOrWrite, confidential));
      });
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.concat(permissions);
    } else {
      permissions = [];
      this.writeProjectsFilters = [];
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
        (p) => !(p.module === ModuleTypes.Planning && p.type === filterType && p.write && p.confidential == confidential)
      );
    }
  }

  /**
   * onFilterChange
   * 1.Remove All permissions that are similar
   * 2.forEach new filters we generate permission object
   * 3.Push new filters(permissions) in employee permissions
   * 4.set filters
   */
  onFiltersChange(
    filters: FilterViewModel[],
    moduleType: ModuleTypes,
    filterType: FilterTypes,
    depVisibility: DepartmentVisibilities,
    readOrWrite: ReadWriteTypes,
    confidential: boolean
  ) {
    const permissions = [];
    // Remove all permissions with those props
    if (readOrWrite === ReadWriteTypes.read) {
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
        (p) => !(p.module === moduleType && p.type === filterType && p.read && !p.write && p.confidential == confidential)
      );
    } else {
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
        (p) => !(p.module === moduleType && p.type === filterType && p.write && p.confidential === confidential)
      );
    }
    filters.forEach((f) => {
      permissions.push(this.generatePermission(filterType, f.filterValue, depVisibility, readOrWrite, confidential));
    });
    // this.employeeDetailsViewModel.permissions = [...permissions, this.employeeDetailsViewModel.permissions];
    this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.concat(permissions);
    this.initAllFiltersByType();
    this.isReadAndWriteTheSame();
    // this.setFilters(filters, filterType, readOrWrite, confidential);
  }

  private setFilters(filters: FilterViewModel[], filterType: FilterTypes, readOrWrite: ReadWriteTypes, confidential: boolean) {
    if (filterType === FilterTypes.Project && readOrWrite === ReadWriteTypes.read && confidential == false) {
      this.readProjectsFilters = filters;
    } else if (filterType === FilterTypes.Project && readOrWrite === ReadWriteTypes.write && confidential == false) {
      this.writeProjectsFilters = filters;
    } else if (filterType === FilterTypes.Project && readOrWrite === ReadWriteTypes.write && confidential == true) {
      this.confidentialProjectFilters = filters;
    }
  }

  hasReadPermission(module: ModuleTypes, type: FilterTypes, values: number[], confidential: boolean): boolean {
    let hasWritePermission = false;

    if (this.isInPrimaryRole) {
      hasWritePermission = true;
    } else if (this.employeeDetailsViewModel.permissions) {
      const permission = this.employeeDetailsViewModel.permissions.find(
        (p) =>
          p.module === module &&
          (p.type === type || p.type === FilterTypes.Empty) &&
          p.read &&
          (p.value === 0 || values.indexOf(p.value) !== -1) &&
          p.confidential === confidential
      );
      if (permission) {
        hasWritePermission = true;
      }
    }

    return hasWritePermission;
  }

  hasAnyReadPermission(module: ModuleTypes, type: FilterTypes) {
    let hasEditPermission = false;

    if (this.isInPrimaryRole) {
      hasEditPermission = true;
    } else if (this.employeeDetailsViewModel.permissions) {
      const permission = this.employeeDetailsViewModel.permissions.find(
        (p) => p.module === module && (p.type === type || p.type === FilterTypes.Empty) && p.read && !p.write && p.value != -1
      );

      if (permission) {
        hasEditPermission = true;
      }
    }

    return hasEditPermission;
  }

  hasAnyWritePermission(module: ModuleTypes, type: FilterTypes, confidential: boolean = false) {
    let hasEditPermission = false;

    if (EmployeeUtil.IsAdmin(this.employeeDetailsViewModel)) {
      hasEditPermission = true;
    } else if (this.employeeDetailsViewModel.permissions) {
      const permission = this.employeeDetailsViewModel.permissions.find(
        (p) =>
          p.module === module &&
          (p.type === type || p.type === FilterTypes.Empty) &&
          p.write &&
          p.value !== -1 &&
          p.confidential === confidential
      );

      if (permission) {
        hasEditPermission = true;
      }
    }

    return hasEditPermission;
  }

  hasConfidentialPermission(module: ModuleTypes, type: FilterTypes, values: number[]) {
    let hasWritePermission = false;
    if (this.employeeDetailsViewModel.permissions) {
      const permission = this.employeeDetailsViewModel.permissions.find(
        (p) =>
          p.module === module &&
          (p.type === type || p.type === FilterTypes.Empty) &&
          p.read &&
          (p.value === 0 || values.indexOf(p.value) !== -1) &&
          p.confidential
      );
      if (permission) {
        hasWritePermission = true;
      }
    }
    return hasWritePermission;
  }

  generateUserRole(employeeId: number, employeeRoleType: EmployeeRoleTypes, accountId = 0) {
    const userRole = new EmployeeRole();
    userRole.employeeId = employeeId;
    userRole.roleId = employeeRoleType;
    userRole.accountId = accountId;

    return userRole;
  }

  generatePermission(
    filterType: FilterTypes,
    value: number,
    departmentVisibility: DepartmentVisibilities,
    readOrWrite: ReadWriteTypes,
    confidential: boolean,
    accountId: number = 0
  ) {
    return PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Planning, filterType, value, departmentVisibility, readOrWrite, confidential, accountId);
  }

  isReadAndWriteTheSame() {
    if (
      JSON.stringify(this.writeProjectsFilters) === JSON.stringify(this.readProjectsFilters) &&
      this.readProjectsFilters.length >= 1
    ) {
      return true;
    } else {
      return false;
    }
  }
}
